import { useEffect, lazy, Suspense } from "react";
import "./App.css";
import "./styles/safari-backdrop-fix.css";
import "./utils/safariBackdropFix";
import {
  BrowserRouter as Router,
  Route,
  Routes,
  Navigate,
} from "react-router-dom";
import { themeChange } from "theme-change";
import checkAuth from "./app/auth";
import initializeApp from "./app/init";
import DazzifyLoading from "./features/common/components/DazzifyLoading";
import { useTranslation } from "react-i18next";
import "react-image-crop/dist/ReactCrop.css";
import {
  initializeChunkErrorHandling,
  createLazyComponentWithRetry,
} from "./utils/chunkRetryUtil";

initializeApp();
initializeChunkErrorHandling();

const token = checkAuth();

// Lazy loading for pages with chunk retry functionality
const Layout = lazy(
  createLazyComponentWithRetry(() => import("./containers/Layout"), "Layout")
);
const Login = lazy(
  createLazyComponentWithRetry(() => import("./pages/Login"), "Login")
);
const ForgotPassword = lazy(
  createLazyComponentWithRetry(
    () => import("./pages/ForgotPassword"),
    "ForgotPassword"
  )
);
const Arrive = lazy(
  createLazyComponentWithRetry(() => import("./pages/public/Arrive"), "Arrive")
);
const SelectBrandPage = lazy(
  createLazyComponentWithRetry(
    () => import("./pages/SelectBrandPage"),
    "SelectBrandPage"
  )
);

const Security = lazy(
  createLazyComponentWithRetry(
    () => import("./features/settings/component/security"),
    "Security"
  )
);
const Help = lazy(
  createLazyComponentWithRetry(
    () => import("./features/settings/component/help"),
    "Help"
  )
);
const Ticket = lazy(
  createLazyComponentWithRetry(
    () => import("./features/settings/component/ticket"),
    "Ticket"
  )
);
const Settings = lazy(
  createLazyComponentWithRetry(() => import("./features/settings"), "Settings")
);
const Categories = lazy(
  createLazyComponentWithRetry(
    () => import("./features/categories/categories"),
    "Categories"
  )
);
const Package = lazy(
  createLazyComponentWithRetry(
    () => import("./features/categories/component/packages"),
    "Package"
  )
);
const Service = lazy(
  createLazyComponentWithRetry(
    () => import("./features/categories/component/services"),
    "Service"
  )
);
const Create = lazy(
  createLazyComponentWithRetry(
    () => import("./features/categories/component/create/create"),
    "Create"
  )
);

function App() {
  const { i18n } = useTranslation();

  useEffect(() => {
    themeChange(false);
  }, []);

  useEffect(() => {
    const updateDirection = () => {
      const language = localStorage.getItem("language") || i18n.language;
      const direction = language === "ar" ? "rtl" : "ltr";
      document.documentElement.setAttribute("dir", direction);
      document.documentElement.setAttribute("lang", language);
    };

    // Set initial direction
    updateDirection();

    // Listen for language changes
    i18n.on("languageChanged", updateDirection);

    // Cleanup
    return () => {
      i18n.off("languageChanged", updateDirection);
    };
  }, [i18n]);

  return (
    <Router>
      <Suspense
        fallback={
          <div className="w-full h-screen flex justify-center items-center">
            <DazzifyLoading
              color="#4b108d"
              style={{ width: "180px", height: "180px" }}
            />
          </div>
        }
      >
        <Routes>
          <Route path="/login" element={<Login />} />
          <Route path="/forgot-password" element={<ForgotPassword />} />
          <Route path="/arrive/:token" element={<Arrive />} />
          <Route path="/select-brand" element={<SelectBrandPage />} />

          <Route
            path="/security"
            element={
              <Layout>
                <Security />
              </Layout>
            }
          />
          <Route
            path="/help"
            element={
              <Layout>
                <Help />
              </Layout>
            }
          />

          <Route
            path="/index"
            element={
              <Layout>
                <Settings />
              </Layout>
            }
          />

          <Route
            path="/categories"
            element={
              <Layout>
                <Categories />
              </Layout>
            }
          />
          <Route
            path="/packages"
            element={
              <Layout>
                <Package />
              </Layout>
            }
          />
          <Route
            path="/services"
            element={
              <Layout>
                <Service />
              </Layout>
            }
          />
          <Route
            path="/create"
            element={
              <Layout>
                <Create />
              </Layout>
            }
          />
          <Route
            path="/ticket"
            element={
              <Layout>
                <Ticket />
              </Layout>
            }
          />

          {/* Place new routes over this */}
          <Route path="/app/*" element={<Layout />} />

          <Route
            path="*"
            element={<Navigate to={token ? "/login" : "/login"} replace />}
          />
        </Routes>
      </Suspense>
    </Router>
  );
}

export default App;
